'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import * as Sentry from '@sentry/react';

import Alert from '@components/Widgets/Alert/Alert.react';
import TextArea from '@components/Input/TextArea.react';

import AuthStore from '@stores/AuthStore';
import { getConfig } from '@utils/Env';
import Analytics from '@utils/Analytics';
import { flattenIngredients } from '@utils/Recipe';
import { getRecipeEventProperties } from '@utils/Content';

import './QuickAddIngredients.scss';

export default class QuickLogAiModal extends Component {
    static propTypes = {
        mealType: PropTypes.string,
        date: PropTypes.object,
        onLogFoods: PropTypes.func,
    };

    static contextTypes = {
        editSessionId: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            input: '',
            showModal: props.showModal || false,
            loading: false,
        };
    }

    openModal = () => {

        this.setState({ showModal: true });

        const { mealType, date } = this.props;
        Analytics.openQuickLogAI({
            'Meal Type': mealType,
            'Date': date ? date.format('YYYY-MM-DD') : null,
        });
    };

    closeModal = () => {
        this.setState({ showModal: false, input: '', error: null, loading: false, delay: false });

        const { mealType, date } = this.props;

        if (!this.state.loading) {
            Analytics.closeQuickLogAI({
                'Meal Type': mealType,
                'Date': date ? date.format('YYYY-MM-DD') : null,
            });
        }
    };

    onChange = (event) => {
        this.setState({ input: event.target.value });
    };

    // Number of full import attempts (initial try + retries) before surfacing the error to the user.
    static MAX_IMPORT_ATTEMPTS = 3;
    // Delay between retry attempts, to give a transient backend issue a moment to recover.
    static RETRY_DELAY_MS = 1000;

    importRecipe = async () => {
        let { input } = this.state;
        const { onLogFoods } = this.props;
        const { editSessionId } = this.context;

        input = input.trim();

        if (!input) {
            this.setState({ error: 'Please describe your meal by typing it into the text field above.' });
            return;
        }

        this.setState({ loading: true, error: false, delay: false });
        const timeoutId = setTimeout(() => this.setState({ delay: true }), 30000);

        const payload = { text: input, import_context: 'quick log' };

        let lastError = 'unknown error';

        for (let attempt = 1; attempt <= QuickLogAiModal.MAX_IMPORT_ATTEMPTS; attempt++) {
            // User closed the modal between attempts — abort silently.
            if (!this.state.showModal) {
                clearTimeout(timeoutId);
                return;
            }

            const result = await this.runImportAttempt(payload, editSessionId, attempt);

            // User closed the modal while we were polling — abort and ignore.
            if (result.status === 'cancelled') {
                clearTimeout(timeoutId);
                return;
            }

            if (result.status === 'success') {
                await onLogFoods(result.recipeDetails, result.job);

                Analytics.quickLogAISuccess({
                    ...payload,
                    ...getRecipeEventProperties(result.recipe, result.recipeDetails),
                    'Duration Seconds': result.durationSeconds,
                    'Job ID': result.job.uuid,
                    'Edit Session ID': editSessionId,
                    'Attempt': attempt,
                });

                this.setState({ showModal: false, loading: false, error: false, delay: false, input: '' });
                clearTimeout(timeoutId);
                return;
            }

            // Failure: remember the error and retry if it's a retryable failure and we have attempts left.
            lastError = result.error;

            if (result.retryable && attempt < QuickLogAiModal.MAX_IMPORT_ATTEMPTS) {
                await new Promise((resolve) => setTimeout(resolve, QuickLogAiModal.RETRY_DELAY_MS));
                continue;
            }

            break;
        }

        // All attempts exhausted (or a non-retryable failure) — show the error exactly as before.
        this.setState({
            loading: false,
            error: lastError || 'unknown error',
            delay: false,
        });

        clearTimeout(timeoutId);
    };

    // Runs a single import attempt (POST + poll). Returns an outcome object; never sets terminal UI state
    // itself, so the caller can decide whether to retry. Analytics are fired per attempt, exactly as before.
    runImportAttempt = async (payload, editSessionId, attempt) => {
        const start = performance.now();

        try {
            const response = await AuthStore.fetch(getConfig('recipe_api') + '/import-recipe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; schema=import/recipe/text/1' },
                body: JSON.stringify(payload),
            });

            if (!response?.links?.self?.href) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: response?.error,
                    'Edit Session ID': editSessionId,
                    'Attempt': attempt,
                });

                return { status: 'error', error: response?.error || 'unknown error', retryable: true };
            }

            Analytics.startQuickLogAI({
                ...payload,
                'Job UUID': response?.uuid,
                'Edit Session ID': editSessionId,
                'Attempt': attempt,
            });

            let jobUrl = getConfig('recipe_api') + response?.links.self.href;
            let job = await AuthStore.fetch(jobUrl);
            let governor = 500;

            while (job?.status !== 'completed' && job?.status !== 'failed' && governor-- > 0) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                job = await AuthStore.fetch(jobUrl);

                if (!this.state.showModal) {
                    // user closed the modal, abort and ignore.
                    Analytics.cancelQuickLogAI({
                        ...payload,
                        'Job ID': job?.uuid,
                        'Attempt': attempt,
                    });
                    return { status: 'cancelled' };
                }
            }

            if (job?.status !== 'completed' || job?.output?.error) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: job?.output?.error,
                    'Job ID': job?.uuid,
                    'Edit Session ID': editSessionId,
                    'Attempt': attempt,
                });

                return { status: 'error', error: job?.output?.error || 'unknown error', retryable: true };
            }

            const { recipe, details: recipeDetails } = job?.output;

            const ingredients = recipeDetails?.ingredients?.flatMap((ingredient) => ingredient.items) || [];

            if (!ingredients.length) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: 'No foods returned',
                    'Job ID': job?.uuid,
                    'Edit Session ID': editSessionId,
                    'Attempt': attempt,
                });

                // Deterministic outcome (the model parsed the text but found no foods) — retrying would
                // re-run the same work and almost certainly return the same result, so don't retry it.
                return {
                    status: 'error',
                    error: 'Unable to recognize food items. Please provide more details and try again.',
                    retryable: false,
                };
            }

            const end = performance.now();
            const durationSeconds = Math.round(((end - start) / 1000) * 100) / 100;

            return { status: 'success', recipe, recipeDetails, job, durationSeconds };
        } catch (error) {
            // A thrown fetch/network error (or unexpected exception) — capture it and treat as a
            // retryable failure so the caller can retry and, after exhausting attempts, reset loading
            // instead of leaving the modal spinning forever.
            Analytics.quickLogAIError({
                ...payload,
                error: error?.message || 'unexpected error',
                'Edit Session ID': editSessionId,
                'Attempt': attempt,
            });

            return { status: 'error', error: 'unknown error', retryable: true };
        }
    };

    renderLoader = () => {
        const { loading } = this.state;

        if (!loading) {
            return;
        }

        return (
            <div className="ingredients-loading">
                <i className="icon-spinner2" />
            </div>
        );
    };

    getErrorForDisplay = () => {
        const { error } = this.state;

        if (!error) {
            return;
        }

        if (error === 'Unable to import: Raw nutrition information is not supported at this time') {
            return <>Please enter the food and amount (e.g., '1 apple'), instead of nutritional values.</>
        } else if (error === 'Unable to import: Questions are not supported at this time') {
            return <>Please enter the food and amount (e.g., '1 apple')</>
        } else if (error === 'Unable to import: Unable to extract a food log or recipe from this text/plain') {
            return <>Unable to recognize the food or amount. Please enter the food and amount clearly (e.g., '1 apple' or '100g chicken').</>
        } else if (error === 'Unable to import: Can\'t extract a food log or recipe from this document.') {
            return <>Unable to recognize the food or amount. Please enter the food and amount clearly (e.g., '1 apple' or '100g chicken').</>
        } else if (error === 'Unable to import: QuickLog AI is currently unavailable') {
            Sentry.withScope(scope => {
                const sentryError = new Error(error);
                sentryError.name = "QuickLogAIUnavailableError";
                Sentry.captureException(sentryError);
            });
            return (
            <>
                We're sorry. QuickLog AI is currently unavailable. We apologize for the inconvenience and are working quickly to restore service.
                Please try again in a little while. For assistance, contact support@eatlove.is.
            </>
            );
        }

        return <>Unable to recognize food items. Please provide more details and try again.</>;
    }

    renderModal = () => {
        const { showModal, input, loading, delay } = this.state;

        if (!showModal) {
            return;
        }

        const error = this.getErrorForDisplay();

        return (
            <Modal
                isOpen={true}
                onRequestClose={this.closeModal}
                contentLabel="Describe Your Meal"
                className={`el-modal el-modal3 el-modal3-centered`}
                overlayClassName={`el-modal-overlay`}
                closeTimeoutMS={250}
            >
                <div className="el-modal-container el-modal3-container">
                    {loading ? (
                        <div className="el-modal-loading el-fonts ingredients-loader" data-testid="ingredients-loader">
                            <h4>Please wait</h4>
                            <i className="icon-spinner2" />
                            <span className="loading-text">Analyzing your meal to create log entries...</span>
                        </div>
                    ) : (
                        <>
                            <header>
                                <h2>Describe Your Meal</h2>
                                <button className="el-modal-close-x" onClick={this.closeModal}>
                                    <i className="icon-close-x" />
                                    <span className="assistive-text">Close Modal</span>
                                </button>
                            </header>
                            <div className="el-modal-body-container el-modal3-body-container el-form el-fonts">
                                <TextArea
                                    autoFocus
                                    maxLength={5000}
                                    textareaClassName="quick-add-ingredients-textarea"
                                    placeholder={
                                        'Describe your meal by using text or voice using the microphone button on your keyboard (if available).\n\nExample: 1 cheeseburger, small fries, 1 cup of broccoli, 1 cup of milk'
                                    }
                                    defaultValue={input}
                                    onChange={this.onChange}
                                />
                                {error && <Alert type="error">{error}</Alert>}
                            </div>
                        </>
                    )}

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                            Cancel
                        </button>
                        <button disabled={loading} className="el-modal-ok-btn" onClick={this.importRecipe}>
                            Log
                        </button>
                    </footer>
                </div>
            </Modal>
        );
    };

    render() {
        return (
            <>
                <button className="add-food-btn quick-log-btn" onClick={this.openModal}>
                    Quick Log <em className="add-food-btn ai-false-btn">AI</em>
                </button>
                {this.renderModal()}
            </>
        );
    }
}
